import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Search, Loader2 } from "lucide-react";
import { searchLessons } from "@/lib/academy.functions";

type Hit = Awaited<ReturnType<typeof searchLessons>>[number];

export function AcademySearch() {
  const search = useServerFn(searchLessons);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<Hit[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (q.trim().length < 2) {
      setHits([]);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await search({ data: { q } });
        setHits(r);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, search]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div ref={wrapRef} className="relative mt-8 w-full max-w-2xl">
      <div className="flex items-center gap-3 rounded-full bg-white px-5 py-3 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.35)]">
        <Search className="h-5 w-5 text-charcoal-500" strokeWidth={1.6} />
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Search lessons, glossary, questions..."
          className="w-full bg-transparent font-sans text-[15px] text-charcoal-900 outline-none placeholder:text-charcoal-500"
          maxLength={200}
        />
        {loading && <Loader2 className="h-4 w-4 animate-spin text-bronze-600" />}
      </div>
      {open && q.trim().length >= 2 && (
        <div
          className="absolute left-0 right-0 top-full z-30 mt-2 overflow-hidden bg-white shadow-[0_24px_60px_-20px_rgba(0,0,0,0.45)]"
          style={{ borderRadius: 6 }}
        >
          {hits.length === 0 && !loading ? (
            <div className="px-5 py-4 font-sans text-[14px] text-charcoal-500">
              No lessons match "{q}" yet.
            </div>
          ) : (
            <ul>
              {hits.map((h) => (
                <li key={h.id}>
                  <Link
                    to="/academy/tracks/$trackSlug/lessons/$lessonSlug"
                    params={{
                      trackSlug: h.track?.slug ?? "",
                      lessonSlug: h.slug,
                    }}
                    onClick={() => setOpen(false)}
                    className="block border-b border-cream-50 px-5 py-3 last:border-0 hover:bg-cream-50"
                  >
                    <p className="font-display text-[15px] text-navy-900">{h.title}</p>
                    {h.track && (
                      <p className="mt-0.5 font-sans text-[11px] uppercase tracking-[0.18em] text-bronze-600">
                        {h.track.title}
                      </p>
                    )}
                    {h.excerpt && (
                      <p className="mt-1 line-clamp-1 font-sans text-[13px] text-charcoal-500">
                        {h.excerpt}
                      </p>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default AcademySearch;